import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { getSteps, getAvailableStepsSequences } from '../configs/stepper';
import { BookingMode, type Step } from '../types/order-main';

export function setActive(step: Step) {
  if (step) {
    step.isActive = true;
    step.isCompleted = false;
  }
}

export function setCompleted(step: Step) {
  step.isActive = false;
  step.isCompleted = true;
}

export function resetStep(step: Step) {
  step.isActive = false;
  step.isCompleted = false;
}

const useStepsStore = defineStore('steps', () => {
  const sequences = ref<number[]>([]);
  const steps = ref<Step[]>([]);

  const hasPreviousStep = computed<boolean>(() => {
    const activeStep = steps.value.find(({ isActive }) => isActive);
    const activeSequence = activeStep?.sequence ?? -1;
    return steps.value.length > 0 && activeSequence > steps.value[0]!.sequence;
  });

  const hasNextStep = computed<boolean>(() => {
    const activeStep = steps.value.find(({ isActive }) => isActive);
    const activeSequence = activeStep?.sequence ?? Infinity;
    return steps.value.length > 0 && activeSequence < steps.value[steps.value.length - 1]!.sequence;
  });

  function stepBySequence(sequence: number): Step | undefined {
    return steps.value.find(step => step.sequence === sequence);
  }

  function configureSteps(mode: BookingMode) {
    steps.value = getSteps(mode);
    sequences.value = getAvailableStepsSequences(mode);
  }

  function getActiveStep(): Step | undefined {
    if (!steps.value.length || !sequences.value.length) {
      return undefined;
    }
    if (!steps.value.some(({ isActive }) => isActive)) {
      const firstStep = stepBySequence(sequences.value[0]);
      if (firstStep) {
        setActive(firstStep);
      }
    }
    return steps.value.find(({ isActive }) => isActive);
  }

  return {
    sequences,
    steps,
    hasPreviousStep,
    hasNextStep,
    stepBySequence,
    configureSteps,
    getActiveStep,
  };
});

export default useStepsStore;
